function isPlainObject(value: unknown): value is Record<string, unknown> {
    if (value === null || typeof value !== 'object') {
        return false;
    }

    const prototype = Object.getPrototypeOf(value);
    return prototype === Object.prototype || prototype === null;
}

/**
 * Recursively removes `undefined` values from an object so that Firestore
 * `addDoc`/`updateDoc` calls never receive unsupported undefined fields.
 *
 * - Top-level undefined fields are removed.
 * - Nested plain objects are sanitized recursively.
 * - Arrays, dates, timestamps, and `null` values are kept intact.
 *   In this application, arrays only ever contain primitive values
 *   (e.g., `daysOfWeek: number[]`), so undefined values inside array
 *   elements are not a concern.
 *
 * @param obj - The object to sanitize.
 * @returns A new object (shallow copy at each level) with all `undefined`
 *   values removed, including inside nested objects.
 *
 * @example
 * stripUndefined({ a: 1, b: undefined, c: { d: undefined, e: 2 } })
 * // => { a: 1, c: { e: 2 } }
 */
export function stripUndefined<T extends Record<string, any>>(obj: T): Partial<T> {
    return Object.fromEntries(
        Object.entries(obj)
            .filter(([, value]) => value !== undefined)
            .map(([key, value]) => [
                key,
                isPlainObject(value)
                    ? stripUndefined(value)
                    : value
            ])
    ) as Partial<T>;
}
